import { useState, useMemo, useEffect } from 'react'
import { useAccounts } from '@/api/hooks/useAccounts'
import { useCTMCampaigns, useCreateCTM, useDeleteCTM, useMessengerProfile, useSaveMessengerProfile } from '@/api/hooks/useCTM'
import type { IceBreaker } from '@/api/hooks/useCTM'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { StatCard } from '@/components/shared/StatCard'
import { toast } from 'sonner'
import { Link2, MousePointerClick, Plus, Copy, Trash2, ExternalLink, Save, MessageSquare, Sparkles, X } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'

export function CTM() {
  const { t, language } = useLanguage()

  function timeAgo(ts: number | null): string {
    if (!ts) return t('Nunca')
    const diff = Date.now() - ts
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return t('Agora')
    if (mins < 60) return `${mins}min`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h`
    const days = Math.floor(hours / 24)
    return `${days}d`
  }
  const { data: accounts, isLoading: loadingAccounts } = useAccounts()
  const [pageId, setPageId] = useState<string>('')
  const { data: campaigns, isLoading: loadingCampaigns } = useCTMCampaigns(pageId || null)
  const { data: profile } = useMessengerProfile(pageId || null)
  const createCTM = useCreateCTM(pageId || null)
  const deleteCTM = useDeleteCTM(pageId || null)
  const saveProfile = useSaveMessengerProfile(pageId || null)

  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')
  const [ref, setRef] = useState('')
  const [welcomeMsg, setWelcomeMsg] = useState('')

  // Messenger Profile state
  const [getStarted, setGetStarted] = useState('CTM_GET_STARTED')
  const [iceBreakers, setIceBreakers] = useState<IceBreaker[]>([
    { question: '', payload: '' },
  ])

  // Sync profile from API
  useEffect(() => {
    if (profile) {
      setGetStarted(profile.get_started?.payload || 'CTM_GET_STARTED')
      const breakers = profile.ice_breakers?.[0]?.call_to_actions || []
      setIceBreakers(
        breakers.length > 0
          ? breakers.map((b) => ({ question: b.question, payload: b.payload }))
          : [{ question: '', payload: '' }],
      )
    }
  }, [profile])

  // Auto-select first account
  const selectedPageId = pageId || accounts?.[0]?.pageId || ''
  if (!pageId && accounts?.[0]?.pageId) {
    setPageId(accounts[0].pageId)
  }

  const stats = useMemo(() => {
    if (!campaigns) return { total: 0, totalClicks: 0 }
    return {
      total: campaigns.length,
      totalClicks: campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0),
    }
  }, [campaigns])

  const handleCreate = () => {
    if (!name.trim() || !ref.trim()) return
    createCTM.mutate(
      { name: name.trim(), ref: ref.trim().replace(/[^a-zA-Z0-9_-]/g, ''), welcomeMessage: welcomeMsg.trim() },
      {
        onSuccess: () => {
          toast.success(t('Campanha criada!'))
          setName('')
          setRef('')
          setWelcomeMsg('')
          setCreateOpen(false)
        },
        onError: (err: any) => toast.error(err?.message || t('Erro ao criar campanha')),
      },
    )
  }

  const handleDelete = (id: string, campaignName: string) => {
    if (!confirm(`${t('Excluir')} "${campaignName}"?`)) return
    deleteCTM.mutate(id, {
      onSuccess: () => toast.success(t('Campanha deletada')),
      onError: () => toast.error(t('Erro ao deletar')),
    })
  }

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link)
    toast.success(t('Link copiado!'))
  }

  const autoRef = (value: string) => {
    setName(value)
    setRef(
      value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9_-]/g, ''),
    )
  }

  const addIceBreaker = () => {
    if (iceBreakers.length >= 4) return
    setIceBreakers([...iceBreakers, { question: '', payload: '' }])
  }

  const removeIceBreaker = (idx: number) => {
    setIceBreakers(iceBreakers.filter((_, i) => i !== idx))
  }

  const updateIceBreaker = (idx: number, field: 'question' | 'payload', value: string) => {
    const updated = [...iceBreakers]
    updated[idx] = { ...updated[idx], [field]: value }
    // Auto-gerar payload a partir da question
    if (field === 'question') {
      updated[idx].payload = `CTM_${value.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '').substring(0, 30)}`
    }
    setIceBreakers(updated)
  }

  const handleSaveProfile = () => {
    const validBreakers = iceBreakers.filter((b) => b.question.trim())
    saveProfile.mutate(
      {
        getStartedPayload: getStarted.trim(),
        iceBreakers: validBreakers,
      },
      {
        onSuccess: () => toast.success(t('Messenger Profile salvo!')),
        onError: (err: any) => toast.error(err?.message || t('Erro ao salvar')),
      },
    )
  }

  if (loadingAccounts) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Click-to-Messenger</h1>
          <p className="text-sm text-muted-foreground">{t('Links + saudacao + botoes para reativar a janela de 24h')}</p>
        </div>
        <div className="flex items-center gap-3">
          {accounts && accounts.length > 1 && (
            <Select value={selectedPageId} onValueChange={setPageId}>
              <SelectTrigger className="h-9 w-48 text-xs">
                <SelectValue placeholder={t('Selecione a pagina')} />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.pageId} value={a.pageId}>
                    {a.name || a.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('Nova Campanha')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('Nova Campanha CTM')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">{t('Nome da campanha')}</Label>
                  <Input value={name} onChange={(e) => autoRef(e.target.value)} placeholder="Ex: Promo Verao 2026" className="h-9 text-xs" />
                </div>
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">{t('Ref (identificador no link)')}</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground shrink-0">m.me/{selectedPageId}?ref=</span>
                    <Input
                      value={ref}
                      onChange={(e) => setRef(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                      placeholder="promo-verao"
                      className="h-9 text-xs"
                    />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 text-xs text-muted-foreground">{t('Mensagem de boas-vindas (opcional)')}</Label>
                  <Textarea
                    value={welcomeMsg}
                    onChange={(e) => setWelcomeMsg(e.target.value)}
                    placeholder={t('Mensagem automatica quando alguem clicar no link...')}
                    className="text-xs min-h-[60px]"
                  />
                </div>
                {ref && (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <Label className="text-[10px] text-muted-foreground">{t('Preview do link:')}</Label>
                    <code className="mt-1 block rounded bg-background px-2 py-1 text-xs break-all">
                      https://m.me/{selectedPageId}?ref={ref}
                    </code>
                  </div>
                )}
                <Button onClick={handleCreate} disabled={createCTM.isPending || !name.trim() || !ref.trim()} className="w-full gap-2">
                  <Link2 className="h-4 w-4" />
                  {createCTM.isPending ? t('Criando...') : t('Criar Campanha')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {language === 'en' && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-2.5">
          <span className="mt-0.5 text-blue-500">&#8505;</span>
          <p className="text-xs text-muted-foreground">Generates m.me links and configures Messenger greeting/ice breakers via the Messenger Profile API. Users initiate contact by clicking the link.</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard label={t('Campanhas ativas')} value={stats.total} icon={Link2} color="blue" />
        <StatCard label={t('Total de cliques')} value={stats.totalClicks} icon={MousePointerClick} color="green" />
      </div>

      {/* Messenger Profile Config */}
      {selectedPageId && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                {t('Configuracao do Messenger')}
              </h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t('Saudacao + botao "Comecar" + opcoes rapidas que aparecem quando alguem abre o chat')}
              </p>
            </div>
            <Button size="sm" className="gap-1.5" onClick={handleSaveProfile} disabled={saveProfile.isPending}>
              <Save className="h-3.5 w-3.5" />
              {saveProfile.isPending ? t('Salvando...') : t('Salvar')}
            </Button>
          </div>

          {/* Greeting info */}
          <div className="rounded-lg bg-muted/30 border border-border px-3 py-2.5">
            <p className="text-xs text-muted-foreground">
              <MessageSquare className="h-3 w-3 inline mr-1" />
              <strong>{t('Saudacao:')}</strong> {t('Configure a mensagem de boas-vindas diretamente no Facebook em')}{' '}
              <span className="text-foreground">{t('Pagina → Configuracoes → Mensagens → Saudacao do Messenger')}</span>
            </p>
          </div>

          {/* Get Started */}
          <div>
            <Label className="mb-1.5 text-xs text-muted-foreground">{t('Botao "Comecar"')}</Label>
            <div className="flex items-center gap-2">
              <Input value={getStarted} onChange={(e) => setGetStarted(e.target.value)} placeholder="CTM_GET_STARTED" className="h-8 text-xs" />
              <Badge variant="outline" className="text-[9px] shrink-0">
                payload
              </Badge>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {t('Quando a pessoa clica "Comecar", envia esse payload e abre a janela de 24h automaticamente.')}
            </p>
          </div>

          {/* Ice Breakers */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs text-muted-foreground">{t('Opcoes rapidas (Ice Breakers)')}</Label>
              {iceBreakers.length < 4 && (
                <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={addIceBreaker}>
                  <Plus className="h-2.5 w-2.5 mr-0.5" /> {t('Adicionar')}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {iceBreakers.map((breaker, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={breaker.question}
                    onChange={(e) => updateIceBreaker(idx, 'question', e.target.value)}
                    placeholder={`${t('Opcao')} ${idx + 1} (ex: "${t('Quero saber mais')}")`}
                    className="h-8 text-xs flex-1"
                  />
                  {iceBreakers.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground" onClick={() => removeIceBreaker(idx)}>
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {t('Botoes que aparecem no chat. A pessoa clica e ja abre a janela sem precisar digitar.')}
            </p>
          </div>

          {/* Preview */}
          <div className="rounded-lg bg-muted/30 border border-border p-4">
            <Label className="text-[10px] text-muted-foreground mb-2 block">{t('Preview do Messenger')}</Label>
            <div className="rounded-lg bg-background border border-border p-4 max-w-xs mx-auto space-y-3">
              {getStarted && (
                <div className="text-center">
                  <span className="inline-block rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground">{t('Comecar')}</span>
                </div>
              )}
              {iceBreakers.filter((b) => b.question.trim()).length > 0 && (
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {iceBreakers
                    .filter((b) => b.question.trim())
                    .map((b, i) => (
                      <span key={i} className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[10px] text-primary">
                        {b.question}
                      </span>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="text-sm font-semibold mb-2">{t('Como funciona?')}</h3>
        <div className="grid gap-3 sm:grid-cols-3 text-xs text-muted-foreground">
          <div className="flex gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold">1</span>
            <span>
              {t('Configure a saudacao + botoes e crie um link')} <code className="text-[10px] bg-muted px-1 rounded">m.me</code>
            </span>
          </div>
          <div className="flex gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold">2</span>
            <span>{t('Compartilhe o link (posts, stories, bio, ads, WhatsApp)')}</span>
          </div>
          <div className="flex gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold">3</span>
            <span>
              {t('Pessoa clica no botao')} → <strong className="text-foreground">{t('janela de 24h ativa')}</strong> → {t('pode enviar disparos')}
            </span>
          </div>
        </div>
      </div>

      {/* Link direto */}
      {selectedPageId && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">{t('Link direto da pagina')}</h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">{t('Sem rastreamento de campanha')}</p>
            </div>
            <div className="flex items-center gap-2">
              <code className="rounded bg-muted px-3 py-1.5 text-xs">https://m.me/{selectedPageId}</code>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => copyLink(`https://m.me/${selectedPageId}`)}>
                <Copy className="h-3 w-3" /> {t('Copiar')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns Table */}
      {loadingCampaigns ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">{t('Campanha')}</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Link</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-center">{t('Cliques')}</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">{t('Ultimo clique')}</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">{t('Acoes')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!campaigns || campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-16 text-center">
                    <Link2 className="mx-auto mb-2 h-8 w-8 text-muted-foreground/20" />
                    <p className="text-xs text-muted-foreground">{t('Nenhuma campanha criada')}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{t('Clique em "Nova Campanha" para comecar')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <div>
                        <div className="text-sm font-medium">{campaign.name}</div>
                        <div className="text-[10px] text-muted-foreground">ref={campaign.ref}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-muted px-2 py-0.5 text-[10px] break-all">{campaign.link}</code>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={campaign.clicks > 0 ? 'default' : 'secondary'} className="text-[10px]">
                        {campaign.clicks}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{timeAgo(campaign.lastClick)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copyLink(campaign.link)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
                          <a href={campaign.link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(campaign.id, campaign.name)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
